import React from 'react';
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  useLocation,
  Outlet,
} from 'react-router-dom';
import './App.scss';
import './styles/Admin.scss';
import './styles/Layout.scss';
import Header from './components/Header';
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ThermometerPage from './pages/ThermometerPage';
import OAuthCallback from './components/OAuthCallback';
import Nav from './components/Nav';
import AuthInterceptor from './components/AuthInterceptor';
import Setting from './components/setting/Setting';
import SettingAccountPage from './pages/SettingAccontPage';
import Counseling from './pages/CounselingPage';
import CounselingList from './pages/CounselingListPage';
import QnAPage from './pages/QnAPage';
import { AuthProvider, useAuth } from './context/AuthContext';
import { AdminMenuProvider } from './context/AdminMenuContext';
import QnAListPage from './pages/QnAListPage';
import MorePage from './pages/MorePage';
import ProfilePage from './pages/ProfilePage';
import OnboardingEditPage from './pages/OnboardingEditPage';
import OnboardingPage from './pages/OnboardingPage';
import DailyJournalPage from './pages/DailyJournalPage';
import TestListPage from './pages/tests/TestListPage';
import GenericTestPage from './pages/tests/GenericTestPage';
import TestResultPage from './pages/tests/TestResultPage';
import TestResultHistoryPage from './pages/tests/TestResultHistoryPage';
import QnADetailPage from './pages/QnADetailPage';
import NoticeListPage from './pages/NoticeListPage';
import NoticePage from './pages/NoticePage';
import NoticeDetailPage from './pages/NoticeDetailPage';
import FAQListPage from './pages/FAQListPage';
import FAQPage from './pages/FAQPage';
import BigFiveTestPage from './pages/BigFiveTestPage';
import BigFiveTestResultPage from './pages/BigFiveTestResultPage';
import CommunityListPage from './pages/CommunityListPage';
import CommunityPostPage from './pages/CommunityPostPage';
import CommunityWritePage from './pages/CommunityWritePage';
import CounselorRegistrationPage from './pages/CounselorRegistrationPage';

import SocialOnboardingPage from './pages/SocialOnboardingPage';
import GoogleTranslate from './components/language/GoogleTranslate';

import AdminDashboardPage from './pages/admin/AdminDashboardPage';
import AdminUserListPage from './pages/admin/AdminUserListPage';
import AdminUserDetailPage from './pages/admin/AdminUserDetailPage';
import AdminUserFormPage from './pages/admin/AdminUserFormPage';
import AdminCounselorListPage from './pages/admin/AdminCounselorListPage';
import AdminCounselorRegistrationListPage from './pages/admin/AdminCounselorRegistrationListPage';
import AdminCounselorRegistrationDetailPage from './pages/admin/AdminCounselorRegistrationDetailPage';
import AdminDailyJournalListPage from './pages/admin/AdminDailyJournalListPage';
import AdminCounselingHistoryPage from './pages/admin/AdminCounselingHistoryPage';
import AdminCounselingDetailPage from './pages/admin/AdminCounselingDetailPage';
import AdminCommunityListPage from './pages/admin/AdminCommunityListPage';
import AdminCommunityDetailPage from './pages/admin/AdminCommunityDetailPage';
import AdminTestResultListPage from './pages/admin/AdminTestResultListPage';
import AdminBigFiveResultListPage from './pages/admin/AdminBigFiveResultListPage';
import AdminBigFiveResultDetailPage from './pages/admin/AdminBigFiveResultDetailPage';
import AdminCommentListPage from './pages/admin/AdminCommentListPage';
import AdminSystemLogPage from './pages/admin/AdminSystemLogPage';
import AdminUserActivityPage from './pages/admin/AdminUserActivityPage';



const MainLayout: React.FC = () => {
  const { isAuthenticated } = useAuth();
  return (
    <>
      {isAuthenticated && <Header />}
      <div className="main-wrapper">
        {isAuthenticated && <Nav />}
        <Outlet />
      </div>
    </>
  );
};

const AppContent: React.FC = () => {
  const { isAuthenticated, user, hasSubmittedToday, loading } = useAuth();
  const location = useLocation();

  if (loading) return null;

  const completed = location.state?.completedOnboarding === true;

  if (
    isAuthenticated &&
    user?.isSocialUser &&
    !user?.hasProvidedAdditionalInfo &&
    !completed && //
    location.pathname !== '/social-onboarding'
  ) {
    return <Navigate to="/social-onboarding" replace />;
  }

  if (
    isAuthenticated &&
    location.pathname === '/social-onboarding' &&
    user?.hasProvidedAdditionalInfo
  ) {
    return <Navigate to="/daily-journal" replace />;
  }

  if (
    isAuthenticated &&
    location.pathname !== '/daily-journal' &&
    location.pathname !== '/social-onboarding' &&
    !location.pathname.startsWith('/admin') && // 관리자 페이지 예외 추가
    hasSubmittedToday === false
  ) {
    return <Navigate to="/daily-journal" replace />;
  }

  return (
    <div className="App">
      <div style={{ display: 'none' }}>
        <GoogleTranslate />
      </div>
      <AuthInterceptor>
        <Routes>
          <Route path="/login/success" element={<OAuthCallback />} />
          <Route path="/login" element={isAuthenticated ? <Navigate to="/" /> : <LoginPage />} />
          <Route
            path="/register"
            element={isAuthenticated ? <Navigate to="/" /> : <RegisterPage />}
          />
          <Route element={<MainLayout />}>
            {/* 하루 한마디 */}
            <Route
              path="/daily-journal"
              element={isAuthenticated ? <DailyJournalPage /> : <Navigate to="/login" />}
            />

            {/* 리본상담 */}
            <Route
              path="/counseling/list"
              element={isAuthenticated ? <CounselingList /> : <Navigate to="/login" />}
            />
            <Route
              path="/counseling/new"
              element={isAuthenticated ? <Counseling /> : <Navigate to="/login" />}
            />
            <Route
              path="/counseling/:id"
              element={isAuthenticated ? <Counseling /> : <Navigate to="/login" />}
            />

            {/* 프로필 */}
            <Route
              path="/profile/edit"
              element={isAuthenticated ? <SettingAccountPage /> : <Navigate to="/login" />}
            />
            <Route
              path="/profile/onboarding-edit"
              element={isAuthenticated ? <OnboardingEditPage /> : <Navigate to="/login" />}
            />
            <Route
              path="/onboarding"
              element={isAuthenticated ? <OnboardingPage /> : <Navigate to="/login" />}
            />
            <Route
              path="/social-onboarding"
              element={isAuthenticated ? <SocialOnboardingPage /> : <Navigate to="/login" />}
            />
            <Route
              path="/profile"
              element={isAuthenticated ? <ProfilePage /> : <Navigate to="/login" />}
            />
            <Route
              path="/profile/register-counselor"
              element={isAuthenticated ? <CounselorRegistrationPage /> : <Navigate to="/login" />}
            />

            {/* 모바일 네비 더보기 창 */}
            <Route
              path="/more"
              element={isAuthenticated ? <MorePage /> : <Navigate to="/login" />}
            />

            {/* 설정 */}
            <Route path="/setting" element={isAuthenticated ? <Setting /> : <Navigate to="/" />} />
            <Route
              path="/setting/qna/new"
              element={isAuthenticated ? <QnAPage /> : <Navigate to="/" />}
            />
            <Route
              path="/setting/qna/:id"
              element={isAuthenticated ? <QnADetailPage /> : <Navigate to="/" />}
            />
            <Route
              path="/setting/qna-list"
              element={isAuthenticated ? <QnAListPage /> : <Navigate to="/" />}
            />
            <Route
              path="/setting/notice"
              element={isAuthenticated ? <NoticeListPage /> : <Navigate to="/" />}
            />
            <Route
              path="/setting/notice/new"
              element={isAuthenticated ? <NoticePage /> : <Navigate to="/" />}
            />
            <Route
              path="/setting/notice/:id"
              element={isAuthenticated ? <NoticeDetailPage /> : <Navigate to="/" />}
            />
            <Route
              path="/setting/faq-list"
              element={isAuthenticated ? <FAQListPage /> : <Navigate to="/" />}
            />
            <Route
              path="/setting/faq/new"
              element={isAuthenticated ? <FAQPage /> : <Navigate to="/" />}
            />
            <Route
              path="/setting/faq/:id"
              element={isAuthenticated ? <FAQPage /> : <Navigate to="/" />}
            />

            <Route
              path="/thermometer"
              element={isAuthenticated ? <ThermometerPage /> : <Navigate to="/login" />}
            />

            {/*심리검사 라우터*/}
            <Route
              path="/tests"
              element={isAuthenticated ? <TestListPage /> : <Navigate to="/login" />}
            />
            <Route
              path="/tests/result"
              element={isAuthenticated ? <TestResultPage /> : <Navigate to="/login" />}
            />
            <Route
              path="/tests/:testId"
              element={isAuthenticated ? <GenericTestPage /> : <Navigate to="/login" />}
            />
            <Route
              path="/tests/:testType/history"
              element={isAuthenticated ? <TestResultHistoryPage /> : <Navigate to="/login" />}
            />

            <Route
              path="/big-five-test"
              element={isAuthenticated ? <BigFiveTestPage /> : <Navigate to="/login" />}
            />
            <Route
              path="/big-five-test-result"
              element={isAuthenticated ? <BigFiveTestResultPage /> : <Navigate to="/login" />}
            />

            {/* 커뮤니티 */}
            <Route
              path="/community"
              element={isAuthenticated ? <CommunityListPage /> : <Navigate to="/login" />}
            />
            <Route
              path="/community/:id"
              element={isAuthenticated ? <CommunityPostPage /> : <Navigate to="/login" />}
            />
            <Route
              path="/community/write"
              element={isAuthenticated ? <CommunityWritePage /> : <Navigate to="/login" />}
            />
            <Route
              path="/community/edit/:id"
              element={isAuthenticated ? <CommunityWritePage /> : <Navigate to="/login" />}
            />

            {/* 관리자 페이지 */}
            <Route
              path="/admin"
              element={
                isAuthenticated && user?.role === 'admin' ? (
                  <AdminDashboardPage />
                ) : (
                  <Navigate to="/" />
                )
              }
            />
            <Route
              path="/admin/users"
              element={
                isAuthenticated && user?.role === 'admin' ? (
                  <AdminUserListPage />
                ) : (
                  <Navigate to="/" />
                )
              }
            />
            <Route
              path="/admin/users/register"
              element={
                isAuthenticated && user?.role === 'admin' ? (
                  <AdminUserFormPage />
                ) : (
                  <Navigate to="/" />
                )
              }
            />
            <Route
              path="/admin/users/:id/edit"
              element={
                isAuthenticated && user?.role === 'admin' ? (
                  <AdminUserFormPage />
                ) : (
                  <Navigate to="/" />
                )
              }
            />
            <Route
              path="/admin/users/:id"
              element={
                isAuthenticated && user?.role === 'admin' ? (
                  <AdminUserDetailPage />
                ) : (
                  <Navigate to="/" />
                )
              }
            />
            <Route
              path="/admin/counselors"
              element={
                isAuthenticated && user?.role === 'admin' ? (
                  <AdminCounselorListPage />
                ) : (
                  <Navigate to="/" />
                )
              }
            />
            <Route
              path="/admin/counselor-registrations"
              element={
                isAuthenticated && user?.role === 'admin' ? (
                  <AdminCounselorRegistrationListPage />
                ) : (
                  <Navigate to="/" />
                )
              }
            />
            <Route
              path="/admin/counselor-registrations/:id"
              element={
                isAuthenticated && user?.role === 'admin' ? (
                  <AdminCounselorRegistrationDetailPage />
                ) : (
                  <Navigate to="/" />
                )
              }
            />
            <Route
              path="/admin/comment"
              element={
                isAuthenticated && user?.role === 'admin' ? (
                  <AdminCommentListPage />
                ) : (
                  <Navigate to="/" />
                )
              }
            />
            <Route
              path="/admin/daily-quotes"
              element={
                isAuthenticated && user?.role === 'admin' ? (
                  <AdminDailyJournalListPage />
                ) : (
                  <Navigate to="/" />
                )
              }
            />
            <Route
              path="/admin/counseling-history"
              element={
                isAuthenticated && user?.role === 'admin' ? (
                  <AdminCounselingHistoryPage />
                ) : (
                  <Navigate to="/" />
                )
              }
            />
            <Route
              path="/admin/counseling-history/:id"
              element={
                isAuthenticated && user?.role === 'admin' ? (
                  <AdminCounselingDetailPage />
                ) : (
                  <Navigate to="/" />
                )
              }
            />
            <Route
              path="/admin/community"
              element={
                isAuthenticated && user?.role === 'admin' ? (
                  <AdminCommunityListPage />
                ) : (
                  <Navigate to="/" />
                )
              }
            />
            <Route
              path="/admin/community/:id"
              element={
                isAuthenticated && user?.role === 'admin' ? (
                  <AdminCommunityDetailPage />
                ) : (
                  <Navigate to="/" />
                )
              }
            />
            <Route
              path="/admin/test-results"
              element={
                isAuthenticated && user?.role === 'admin' ? (
                  <AdminTestResultListPage />
                ) : (
                  <Navigate to="/" />
                )
              }
            />
            <Route
              path="/admin/big-five-results"
              element={
                isAuthenticated && user?.role === 'admin' ? (
                  <AdminBigFiveResultListPage />
                ) : (
                  <Navigate to="/" />
                )
              }
            />
            <Route
              path="/admin/big-five-results/:id"
              element={
                isAuthenticated && user?.role === 'admin' ? (
                  <AdminBigFiveResultDetailPage />
                ) : (
                  <Navigate to="/" />
                )
              }
            />
            <Route
              path="/admin/logs"
              element={
                isAuthenticated && user?.role === 'admin' ? (
                  <AdminSystemLogPage />
                ) : (
                  <Navigate to="/" />
                )
              }
            />
            <Route
              path="/admin/activity-logs"
              element={
                isAuthenticated && user?.role === 'admin' ? (
                  <AdminUserActivityPage />
                ) : (
                  <Navigate to="/" />
                )
              }
            />

            {/* 기본 화면 */}
            <Route path="/" element={isAuthenticated ? <HomePage /> : <Navigate to="/login" />} />
          </Route>
        </Routes>
      </AuthInterceptor>
    </div>
  );
};

const App: React.FC = () => {
  return (
    <Router>
      <AuthProvider>
        <AdminMenuProvider>
          <AppContent />
        </AdminMenuProvider>
      </AuthProvider>
    </Router>
  );
};

export default App;
